import React, { useState, useEffect } from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import FAQ from 'components/ui/FAQ';
import RelatedBlogs from 'components/RelatedBlogs';
import { aiIntegrationFAQs } from '../data/faqs';
import { sendRequirementsFormEmail } from 'utils/emailService';
import { useNavigate } from 'react-router-dom';
import { useContactInfo } from '../utils/contactObfuscation';

const AIMLServices = () => {
  const contactInfo = useContactInfo();
  const [isCalendlyLoaded, setIsCalendlyLoaded] = useState(false);
  const [calendlyError, setCalendlyError] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    requirements: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Enhanced SEO Data
  const seoData = {
    title: 'AI & ML Services - Machine Learning, NLP, Computer Vision | OctalChip',
    description: 'Comprehensive AI and Machine Learning services including ML development, NLP, computer vision, predictive analytics, chatbots, deep learning, and data science solutions.',
    keywords: 'AI services, machine learning services, NLP services, computer vision, predictive analytics, AI chatbots, deep learning, data science, AI consulting, ML development',
    canonical: 'https://www.octalchip.com/ai-ml-services',
    tags: ['AI services', 'machine learning', 'NLP', 'computer vision', 'predictive analytics'],
    additionalMeta: [
      { name: 'theme-color', content: '#00d690' },
      { name: 'mobile-web-app-capable', content: 'yes' }
    ],
    breadcrumbs: [
      { name: 'Home', url: 'https://www.octalchip.com/' },
      { name: 'AI & ML Services', url: 'https://www.octalchip.com/ai-ml-services' }
    ],
    faqData: aiIntegrationFAQs,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "AI & ML Services",
      "description": "Comprehensive AI and Machine Learning services including ML development, NLP, computer vision, and predictive analytics",
      "url": "https://www.octalchip.com/ai-ml-services",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip",
        "url": "https://www.octalchip.com",
        "logo": "https://www.octalchip.com/favicon.svg"
      }
    }
  };

  useEffect(() => {
    if (window.Calendly) {
      setIsCalendlyLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      setIsCalendlyLoaded(true);
      setCalendlyError(false);
    };
    script.onerror = () => {
      console.error('Failed to load Calendly script');
      setCalendlyError(true);
    };
    
    document.head.appendChild(script);

    const timeoutId = setTimeout(() => {
      if (!window.Calendly) {
        console.warn('Calendly script took too long to load');
        setCalendlyError(true);
      }
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    if (isCalendlyLoaded && window.Calendly) {
      setTimeout(() => {
        const widget = document.querySelector('.calendly-inline-widget');
        if (widget && !widget.hasAttribute('data-processed')) {
          try {
            window.Calendly.initInlineWidget({
              url: 'https://calendly.com/business-octalchip/30min',
              parentElement: widget
            });
          } catch (error) {
            console.error('Error initializing Calendly widget:', error);
          }
        }
      }, 500);
    }
  }, [isCalendlyLoaded]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const result = await sendRequirementsFormEmail(formData);
      
      if (result.success) {
        setSubmitMessage('Thank you! Your requirements have been submitted successfully. We\'ll get back to you soon.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          projectType: '',
          requirements: ''
        });
      } else {
        setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your requirements. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const aiMLServices = [
    {
      title: 'Machine Learning Development',
      description: 'Build custom ML models for prediction, classification, and pattern recognition. We develop scalable machine learning solutions tailored to your business needs.',
      icon: 'Brain',
      features: ['Custom ML Models', 'Model Training & Optimization', 'Feature Engineering', 'Model Deployment', 'MLOps Pipeline', 'Performance Monitoring'],
      technologies: ['Python', 'TensorFlow', 'PyTorch', 'Scikit-learn', 'XGBoost', 'MLflow', 'Pandas', 'NumPy'],
      projects: '15+',
      timeline: '4-10 weeks',
      path: '/services/ai-ml-services/machine-learning',
      useCases: ['Predictive Modeling', 'Classification Systems', 'Anomaly Detection', 'Recommendation Engines', 'Time Series Forecasting', 'Clustering Analysis']
    },
    {
      title: 'Natural Language Processing',
      description: 'Advanced NLP solutions for text analysis, sentiment analysis, language translation, and intelligent text processing. Transform unstructured text into actionable insights.',
      icon: 'FileText',
      features: ['Text Classification', 'Sentiment Analysis', 'Named Entity Recognition', 'Language Translation', 'Text Generation', 'Chatbot Development'],
      technologies: ['Transformers', 'BERT', 'GPT', 'spaCy', 'NLTK', 'Hugging Face', 'OpenAI API', 'TensorFlow'],
      projects: '12+',
      timeline: '3-8 weeks',
      path: '/services/ai-ml-services/natural-language-processing',
      useCases: ['Customer Feedback Analysis', 'Content Moderation', 'Document Classification', 'Language Translation', 'Text Summarization', 'Intent Recognition']
    },
    {
      title: 'Computer Vision',
      description: 'Image and video analysis solutions for object detection, recognition, classification, and content moderation. Leverage visual data to automate processes.',
      icon: 'Eye',
      features: ['Object Detection', 'Image Classification', 'Face Recognition', 'OCR', 'Video Analysis', 'Content Moderation'],
      technologies: ['OpenCV', 'YOLO', 'ResNet', 'CNN', 'TensorFlow', 'PyTorch'],
      projects: '10+',
      timeline: '3-8 weeks',
      path: '/services/ai-ml-services/computer-vision',
      useCases: ['Quality Control', 'Security & Surveillance', 'Medical Imaging', 'Autonomous Vehicles', 'Retail Analytics', 'Document Processing']
    },
    {
      title: 'Predictive Analytics',
      description: 'Data-driven forecasting and predictive modeling to make informed business decisions. Analyze trends, predict outcomes, and optimize operations.',
      icon: 'TrendingUp',
      features: ['Forecasting Models', 'Risk Analysis', 'Demand Forecasting', 'Churn Prediction', 'Sales Forecasting', 'Performance Analytics'],
      technologies: ['Python', 'R', 'Pandas', 'NumPy', 'Prophet', 'ARIMA'],
      projects: '18+',
      timeline: '3-8 weeks',
      path: '/services/ai-ml-services/predictive-analytics',
      useCases: ['Sales Forecasting', 'Inventory Management', 'Customer Churn Prediction', 'Risk Assessment', 'Market Analysis', 'Operational Optimization']
    },
    {
      title: 'AI Chatbots & Virtual Assistants',
      description: 'Intelligent conversational AI solutions for customer support, sales, and engagement. Build chatbots that understand context and provide human-like interactions.',
      icon: 'MessageCircle',
      features: ['Conversational AI', 'Multi-language Support', 'Context Understanding', 'Integration APIs', 'Analytics Dashboard', '24/7 Availability'],
      technologies: ['OpenAI GPT', 'Dialogflow', 'Rasa', 'Microsoft Bot', 'Watson Assistant', 'Custom NLP'],
      projects: '20+',
      timeline: '2-6 weeks',
      path: '/services/ai-ml-services/ai-chatbots',
      useCases: ['Customer Support', 'Sales Assistance', 'Lead Qualification', 'FAQ Automation', 'Appointment Booking', 'E-commerce Support']
    },
    {
      title: 'Deep Learning Solutions',
      description: 'Advanced deep learning models using neural networks for complex pattern recognition, image processing, and sequence modeling. Solve complex AI challenges.',
      icon: 'Network',
      features: ['Neural Networks', 'CNN & RNN', 'Transfer Learning', 'GANs', 'Autoencoders', 'Reinforcement Learning'],
      technologies: ['TensorFlow', 'PyTorch', 'Keras', 'Caffe', 'MXNet', 'ONNX'],
      projects: '14+',
      timeline: '6-12 weeks',
      path: '/services/ai-ml-services/deep-learning',
      useCases: ['Image Recognition', 'Speech Recognition', 'Natural Language Understanding', 'Autonomous Systems', 'Generative Models', 'Complex Pattern Recognition']
    },
    {
      title: 'Data Science & Analytics',
      description: 'Comprehensive data science services including data analysis, statistical modeling, and business intelligence. Turn data into actionable business insights.',
      icon: 'BarChart',
      features: ['Data Analysis', 'Statistical Modeling', 'Data Visualization', 'Business Intelligence', 'Data Mining', 'Report Generation'],
      technologies: ['Python', 'R', 'SQL', 'Tableau', 'Power BI', 'Jupyter'],
      projects: '22+',
      timeline: '2-8 weeks',
      path: '/services/ai-ml-services/data-science',
      useCases: ['Business Intelligence', 'Market Research', 'Customer Analytics', 'Operational Analytics', 'Financial Analysis', 'Performance Dashboards']
    },
    {
      title: 'AI Consulting & Strategy',
      description: 'Strategic AI consulting to identify opportunities, develop AI roadmaps, and implement AI solutions that drive business value and competitive advantage.',
      icon: 'Lightbulb',
      features: ['AI Strategy', 'Opportunity Assessment', 'ROI Analysis', 'Technology Selection', 'Implementation Roadmap', 'Training & Support'],
      technologies: ['AI Frameworks', 'Cloud AI Services', 'ML Platforms', 'Data Infrastructure', 'Model Deployment', 'AI Analytics Tools'],
      projects: '30+',
      timeline: '2-6 weeks',
      path: '/services/ai-ml-services/ai-consulting',
      useCases: ['AI Transformation', 'Digital Strategy', 'Process Automation', 'Innovation Consulting', 'Technology Assessment', 'AI Training']
    },
    {
      title: 'Reinforcement Learning',
      description: 'Build intelligent reinforcement learning agents that learn optimal strategies through interaction. We develop RL solutions for autonomous systems, game AI, robotics, and complex optimization problems.',
      icon: 'Network',
      features: ['RL Agent Development', 'Deep Q-Networks (DQN)', 'Policy Gradient Methods', 'Multi-Agent Systems', 'Environment Simulation', 'Reward Engineering'],
      technologies: ['PyTorch', 'TensorFlow', 'OpenAI Gym', 'Stable Baselines3', 'Ray RLlib', 'Unity ML-Agents', 'TensorFlow Agents', 'Python'],
      projects: '10+',
      timeline: '6-14 weeks',
      path: '/services/ai-ml-services/reinforcement-learning',
      useCases: ['Game AI & Strategy', 'Robotics & Control', 'Autonomous Vehicles', 'Resource Optimization', 'Trading & Finance', 'Recommendation Systems']
    }
  ];

  const pricingPlans = [
    {
      title: 'HOURLY ENGAGEMENT',
      price: '$25',
      period: 'Per Hour',
      description: 'Pay-as-you-go. Perfect for AI/ML projects and MVPs.',
      features: [
        '3–5 Yrs Experienced AI/ML Engineers',
        'Daily Progress Updates',
        'Ideal for Quick Turnarounds'
      ],
      popular: false
    },
    {
      title: 'MONTHLY DEDICATED DEV',
      price: '$3200',
      period: 'Per Month (160 hrs)',
      description: 'Hire a full-time AI/ML expert dedicated to your project.',
      features: [
        'AI/ML Specialist',
        'Team Collaboration',
        'Priority Support'
      ],
      popular: true
    },
    {
      title: 'FIXED-COST PROJECTS',
      price: '$$$$',
      period: 'Custom Pricing',
      description: 'Get your complete AI/ML solution delivered within a timeline.',
      features: [
        'Detailed Estimation & Scope',
        'Agile Sprint Milestones',
        'One-Time Payment'
      ],
      popular: false
    }
  ];

  const developmentProcessSteps = [
    {
      phase: 'Discovery & Analysis',
      duration: '1-2 weeks',
      description: 'Understanding your business requirements, data availability, and AI opportunities',
      deliverables: ['Requirements Document', 'AI Strategy', 'Data Assessment', 'Project Timeline'],
      icon: <Icon name="Search" size={24} className="text-blue-600" />
    },
    {
      phase: 'Data Preparation',
      duration: '2-3 weeks',
      description: 'Collecting, cleaning, and preparing data for model training and validation',
      deliverables: ['Cleaned Dataset', 'Feature Engineering', 'Data Pipeline', 'Data Documentation'],
      icon: <Icon name="Database" size={24} className="text-purple-600" />
    },
    {
      phase: 'Model Development',
      duration: '4-8 weeks',
      description: 'Building, training, and optimizing AI/ML models using appropriate algorithms',
      deliverables: ['Trained Models', 'Model Evaluation', 'Performance Metrics', 'Model Documentation'],
      icon: <Icon name="Code" size={24} className="text-green-600" />
    },
    {
      phase: 'Testing & Validation',
      duration: '1-2 weeks',
      description: 'Comprehensive testing with real-world data and validation of accuracy and performance',
      deliverables: ['Test Reports', 'Validation Results', 'Performance Optimization', 'Quality Assurance'],
      icon: <Icon name="CheckCircle" size={24} className="text-orange-600" />
    },
    {
      phase: 'Integration & Deployment',
      duration: '1-2 weeks',
      description: 'Integrating AI models into existing systems and deploying to production',
      deliverables: ['Production Deployment', 'API Integration', 'Monitoring Setup', 'Deployment Documentation'],
      icon: <Icon name="Rocket" size={24} className="text-red-600" />
    },
    {
      phase: 'Monitoring & Optimization',
      duration: 'Ongoing',
      description: 'Continuously monitoring performance and retraining models for better accuracy',
      deliverables: ['Monitoring Dashboards', 'Model Retraining Pipeline', 'Performance Reports', 'Ongoing Support'],
      icon: <Icon name="BarChart" size={24} className="text-gray-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        tags={seoData.tags}
        additionalMeta={seoData.additionalMeta}
        breadcrumbs={seoData.breadcrumbs}
        faqData={seoData.faqData}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Hero Section */}
      <section id="hero" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4 sm:mb-6 animate-fade-in-up px-4">
              AI & ML Services
            </h1>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto animate-fade-in-up animation-delay-200">
              Transform your business with cutting-edge Artificial Intelligence and Machine Learning solutions. 
              From machine learning models to intelligent chatbots, we deliver AI-powered innovations that drive growth.
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">What We Offer</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive AI and Machine Learning solutions designed to transform your business operations
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {aiMLServices.map((service, index) => (
              <div 
                key={index} 
                className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover-lift group relative overflow-hidden cursor-pointer"
                style={{ borderRadius: '12px' }}
                onClick={() => navigate(service.path)}
              >
                {/* Gradient overlay on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-in-out"
                  style={{
                    background: 'linear-gradient(45deg, #00dc88, #00bcd4)',
                    borderRadius: '14px',
                    top: '-2px',
                    left: '-2px',
                    right: '-2px',
                    bottom: '-2px',
                    zIndex: '-1'
                  }}
                ></div>
                
                <div className="relative z-10">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-white/20 transition-all duration-300">
                    <Icon 
                      name={service.icon} 
                      size={24} 
                      className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690] group-hover:text-white transition-all duration-300" 
                    />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 group-hover:text-white mb-3 sm:mb-4 transition-all duration-300">
                    {service.title}
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 group-hover:text-white/90 leading-relaxed transition-all duration-300 mb-3 sm:mb-4">
                    {service.description}
                  </p>
                  
                  {/* Service Stats */}
                  <div className="flex justify-between items-center mb-3 sm:mb-4 text-xs sm:text-sm">
                    <span className="bg-[#00d690]/10 text-[#00d690] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                      {service.projects} projects
                    </span>
                    <span className="bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-xs sm:text-sm group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                      {service.timeline}
                    </span>
                  </div>
                  
                  {/* Key Features */}
                  <div className="mb-3 sm:mb-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-white/90 mb-1.5 sm:mb-2 transition-all duration-300">
                      Key Features:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          {feature}
                        </span>
                      ))}
                      {service.features.length > 3 && (
                        <span className="bg-gray-100 text-gray-600 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          +{service.features.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Technologies */}
                  <div className="mb-3 sm:mb-4">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-white/90 mb-1.5 sm:mb-2 transition-all duration-300">
                      Technologies:
                    </h4>
                    <div className="flex flex-wrap gap-1">
                      {service.technologies.slice(0, 4).map((tech, idx) => (
                        <span key={idx} className="bg-[#00d690]/10 text-[#00d690] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          {tech}
                        </span>
                      ))}
                      {service.technologies.length > 4 && (
                        <span className="bg-[#00d690]/10 text-[#00d690] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs group-hover:bg-white/20 group-hover:text-white transition-all duration-300">
                          +{service.technologies.length - 4} more
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <button 
                    className="w-full bg-[#00d690] hover:bg-[#00b876] text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-300 group-hover:bg-white group-hover:text-[#00d690]"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(service.path);
                    }}
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regular Services Cross-Link Section */}
      <section id="regular-services" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 lg:p-12 border border-[#00d690]/20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
                <div>
                  <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                    <Icon name="Settings" size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-white" />
                  </div>
                  <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
                    Explore Our Development Services
                  </h2>
                  <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mb-4 sm:mb-6"></div>
                  <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                    Need web development, mobile apps, cloud infrastructure, or design services? We offer comprehensive 
                    development solutions including Web Development, Mobile App Development, Cloud & DevOps, UI/UX Design, 
                    Backend Development, and AI Integration.
                  </p>
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 leading-relaxed">
                    From concept to deployment, we deliver scalable, high-performance solutions that drive business growth 
                    and digital transformation.
                  </p>
                  <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Web Development</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Mobile Apps</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Cloud & DevOps</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">UI/UX Design</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">Backend</span>
                    <span className="bg-[#00d690]/10 text-[#00d690] px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">AI Integration</span>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/services')}
                    className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base md:text-lg group relative overflow-hidden transform transition-all duration-300 hover:scale-110 hover:shadow-2xl hover:shadow-[#00d690]/50 active:scale-105"
                    iconName="ChevronRight"
                    iconPosition="right"
                    size="lg"
                  >
                    View All Services
                  </Button>
                </div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-[#00d690]/20 to-[#00b876]/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 h-full flex flex-col justify-center">
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">6</div>
                        <div className="text-xs sm:text-sm text-gray-600">Core Services</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">100+</div>
                        <div className="text-xs sm:text-sm text-gray-600">Projects</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">25+</div>
                        <div className="text-xs sm:text-sm text-gray-600">Developers</div>
                      </div>
                      <div className="bg-white rounded-xl p-4 sm:p-6 shadow-lg text-center">
                        <div className="text-2xl sm:text-3xl font-bold text-[#00d690] mb-1 sm:mb-2">3+</div>
                        <div className="text-xs sm:text-sm text-gray-600">Years Experience</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Development Process Section */}
      <section id="development-process" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">Our Development Process</h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              We follow a proven methodology that ensures quality, transparency, and timely delivery of your project
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-[#00d690]"></div>
              
              {developmentProcessSteps.map((step, index) => (
                <div key={index} className="relative flex items-start mb-8 sm:mb-10 md:mb-12">
                  {/* Timeline Dot */}
                  <div className="absolute left-6 w-3 h-3 sm:w-4 sm:h-4 bg-[#00d690] rounded-full border-2 sm:border-4 border-white shadow-lg"></div>
                  
                  {/* Content */}
                  <div className="ml-12 sm:ml-16 bg-gray-50 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-shadow">
                    <div className="flex items-center mb-3 sm:mb-4">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00d690]/10 rounded-full flex items-center justify-center text-lg sm:text-xl mr-3 sm:mr-4">
                        {step.icon}
                      </div>
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-800">{step.phase}</h3>
                        <p className="text-sm sm:text-base text-[#00d690] font-semibold">{step.duration}</p>
                      </div>
                    </div>
                    
                    <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{step.description}</p>
                    
                    <div>
                      <h4 className="text-sm sm:text-base font-semibold text-gray-800 mb-2">Key Deliverables:</h4>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <span key={idx} className="bg-gray-100 text-gray-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
                            {deliverable}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Share Your Requirements Section */}
      <section id="share-your-requirements" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4 sm:mb-6 animate-fade-in-up px-4">
              Share Your Requirements
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 mb-8 sm:mb-12 animate-fade-in-up animation-delay-200 px-4">
              Tell us about your project and let our expert team create the perfect solution for your business needs.
            </p>
            
            <div className="bg-gray-50 p-4 sm:p-6 md:p-8 rounded-xl shadow-lg">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Name</label>
                    <input 
                      type="text" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent text-sm sm:text-base"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Email</label>
                    <input 
                      type="email" 
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent text-sm sm:text-base"
                      placeholder="your.email@example.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent text-sm sm:text-base"
                    placeholder="Your phone number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Project Type</label>
                  <select 
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent text-sm sm:text-base"
                    required
                  >
                    <option value="">Select project type</option>
                    <option value="Machine Learning">Machine Learning</option>
                    <option value="Natural Language Processing">Natural Language Processing</option>
                    <option value="Computer Vision">Computer Vision</option>
                    <option value="Predictive Analytics">Predictive Analytics</option>
                    <option value="AI Chatbots">AI Chatbots</option>
                    <option value="Deep Learning">Deep Learning</option>
                    <option value="Data Science">Data Science</option>
                    <option value="AI Consulting">AI Consulting</option>
                    <option value="Reinforcement Learning">Reinforcement Learning</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Project Description</label>
                  <textarea 
                    name="requirements"
                    value={formData.requirements}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00d690] focus:border-transparent text-sm sm:text-base"
                    placeholder="Describe your AI/ML project requirements, goals, and timeline..."
                    required
                  ></textarea>
                </div>
                
                {submitMessage && (
                  <div className={`p-3 sm:p-4 rounded-lg text-center text-sm sm:text-base ${
                    submitMessage.includes('Thank you') 
                      ? 'bg-green-100 text-green-700 border border-green-200' 
                      : 'bg-red-100 text-red-700 border border-red-200'
                  }`}>
                    {submitMessage}
                  </div>
                )}
                
                <div className="text-center">
                  <Button 
                    type="submit"
                    variant="primary" 
                    className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base md:text-lg"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Requirements'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Hire Pre-Vetted Developers Section */}
      <section id="pricing" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">
              Hire Pre-Vetted AI/ML Engineers in 48 Hours
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Get access to our pool of experienced, pre-vetted AI/ML engineers ready to join your project within 48 hours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 max-w-6xl mx-auto">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-lg relative ${
                  plan.popular ? 'ring-2 ring-[#00d690]' : ''
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-[#00d690] text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6 sm:mb-8">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{plan.title}</h3>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#00d690] mb-2">{plan.price}</div>
                  <div className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{plan.period}</div>
                  <p className="text-xs sm:text-sm md:text-base text-gray-600">{plan.description}</p>
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <Icon name="Check" size={14} className="sm:w-4 sm:h-4 text-[#00d690] mr-2 sm:mr-3 flex-shrink-0" />
                      <span className="text-xs sm:text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.popular ? "primary" : "outline"}
                  className="w-full"
                  onClick={() => navigate('/contact')}
                >
                  Get Started
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Calendly Meeting Section */}
      <section id="consultation" className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 md:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4 px-4">
              Schedule a Free Consultation
            </h2>
            <div className="w-16 sm:w-20 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 sm:mb-6"></div>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Book a 30-minute consultation with our AI/ML experts to discuss your project requirements and get a personalized quote.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            {calendlyError ? (
              <div className="text-center py-8 sm:py-12 md:py-16 lg:py-20 bg-white rounded-xl shadow-lg">
                <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Icon name="Calendar" size={24} className="sm:w-7 sm:h-7 md:w-8 md:h-8 text-[#00d690]" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-800 mb-3 sm:mb-4">Schedule a Consultation</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
                  We're having trouble loading our calendar widget. Please contact us directly to schedule a consultation.
                </p>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                    <Icon name="Mail" size={16} className="sm:w-5 sm:h-5 text-[#00d690]" />
                    <a href="mailto:business@octalchip.com" className="text-sm sm:text-base text-[#00d690] hover:underline">
                      business@octalchip.com
                    </a>
                  </div>
                  <div className="flex items-center justify-center space-x-3 sm:space-x-4">
                    <Icon name="Phone" size={16} className="sm:w-5 sm:h-5 text-[#00d690]" />
                    {contactInfo?.phoneLink && (
                      <a href={contactInfo.phoneLink} className="text-sm sm:text-base text-[#00d690] hover:underline">
                        {contactInfo.phone?.replace(/\s/g, '-')}
                      </a>
                    )}
                  </div>
                </div>
                <div className="mt-6 sm:mt-8">
                  <Button 
                    variant="primary"
                    onClick={() => window.open('https://calendly.com/business-octalchip/30min', '_blank')}
                    className="px-6 sm:px-8 py-2.5 sm:py-3 text-sm sm:text-base"
                  >
                    Open Calendar in New Tab
                  </Button>
                </div>
              </div>
            ) : isCalendlyLoaded ? (
              <div 
                className="calendly-inline-widget" 
                data-url="https://calendly.com/business-octalchip/30min"
                style={{ 
                  minWidth: '275px', 
                  height: '900px',
                  width: '100%',
                  border: 'none',
                  overflow: 'hidden'
                }}
              ></div>
            ) : (
              <div className="text-center py-8 sm:py-12 md:py-16 lg:py-20">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-[#00d690] mx-auto mb-3 sm:mb-4"></div>
                <p className="text-sm sm:text-base text-gray-600">Loading calendar...</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-2">If the calendar doesn't load, please refresh the page</p>
                <p className="text-[10px] sm:text-xs text-gray-400 mt-2">Debug: isCalendlyLoaded={isCalendlyLoaded.toString()}, calendlyError={calendlyError.toString()}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ 
        faqs={aiIntegrationFAQs}
        title="AI & ML Services FAQs"
        description="Common questions about our AI and machine learning services, capabilities, and solutions."
      />

      {/* Related Blogs Section */}
      <RelatedBlogs
        servicePath="/ai-ml-services"
        serviceTags={['AI', 'Machine Learning', 'NLP', 'Computer Vision', 'Predictive Analytics', 'AI Chatbots', 'Deep Learning', 'Data Science', 'Reinforcement Learning']}
        serviceName="AI & ML Services"
        limit={6}
        title="AI & ML Related Blogs"
      />

      <Footer />
    </div>
  );
};

export default AIMLServices;

